import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  CreditCard,
  Clock,
  CheckCircle,
  XCircle,
  RefreshCw,
  AlertTriangle,
  Eye,
  Play,
  DollarSign,
  Calendar,
  User,
  Mail,
  ExternalLink
} from "lucide-react";
import { Button } from "../../components/ui/button";
import DashboardLayout from "../../components/DashboardLayout";

const API = process.env.REACT_APP_BACKEND_URL;

const StatusBadge = ({ status }) => {
  const styles = {
    initiated: "bg-yellow-100 text-yellow-700 border-yellow-200",
    pending: "bg-yellow-100 text-yellow-700 border-yellow-200",
    paid: "bg-green-100 text-green-700 border-green-200",
    completed: "bg-green-100 text-green-700 border-green-200",
    expired: "bg-gray-100 text-gray-600 border-gray-200",
    failed: "bg-red-100 text-red-700 border-red-200"
  };
  
  const labels = {
    initiated: "Iniciado",
    pending: "Pendiente",
    paid: "Pagado",
    completed: "Completado",
    expired: "Expirado",
    failed: "Fallido"
  };
  
  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full border ${styles[status] || styles.pending}`}>
      {labels[status] || status}
    </span>
  );
};

export default function AdminPaymentsPending() {
  const [pendingPayments, setPendingPayments] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [stats, setStats] = useState({ total: 0, completed: 0, pending: 0 });
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(null);
  const [processing, setProcessing] = useState(null);
  const [activeTab, setActiveTab] = useState("pending");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      
      const [pendingRes, historyRes] = await Promise.all([
        axios.get(`${API}/admin/payments/pending`, { headers }),
        axios.get(`${API}/admin/payments/history?limit=50`, { headers })
      ]);
      
      setPendingPayments(pendingRes.data.pending_transactions || []);
      setPaymentHistory(historyRes.data.transactions || []);
      setStats(historyRes.data.stats || { total: 0, completed: 0, pending: 0 });
    } catch (error) {
      console.error(error);
      toast.error("Error al cargar pagos");
    } finally {
      setLoading(false);
    }
  };

  const verifyPayment = async (transactionId) => {
    setVerifying(transactionId);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API}/admin/payments/verify/${transactionId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const data = res.data;
      
      if (data.payment_status === "paid") {
        toast.success(
          <div>
            <p className="font-bold">¡Pago confirmado en Stripe!</p>
            <p className="text-sm">Email: {data.customer_email}</p>
            <p className="text-sm">Monto: ${data.amount_total} {data.currency?.toUpperCase()}</p>
          </div>
        );
      } else {
        toast.info(
          <div>
            <p className="font-bold">Estado: {data.payment_status}</p>
            <p className="text-sm">Stripe status: {data.stripe_status}</p>
          </div>
        );
      }
      
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al verificar");
    } finally {
      setVerifying(null);
    }
  };

  const processPayment = async (transactionId) => {
    setProcessing(transactionId);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API}/admin/payments/process/${transactionId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(
        <div>
          <p className="font-bold">¡Pago procesado exitosamente!</p>
          <p className="text-sm">Se creó el cliente y caso automáticamente</p>
        </div>
      );
      
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al procesar pago");
    } finally {
      setProcessing(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("es-MX", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const formatAmount = (amount) => {
    if (!amount) return "$0";
    return `$${amount.toLocaleString("en-US")}`;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
              <CreditCard className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-navy-900">
                Gestión de Pagos
              </h1>
              <p className="text-navy-500">Verificar y procesar pagos pendientes</p>
            </div>
          </div>
          
          <Button onClick={fetchData} variant="outline" className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Actualizar
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-navy-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-navy-500">Total Transacciones</p>
                <p className="text-2xl font-bold text-navy-900">{stats.total}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl border border-navy-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-navy-500">Completadas</p>
                <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl border border-navy-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-navy-500">Pendientes</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Alert if pending payments */}
        {pendingPayments.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <p className="font-semibold text-amber-800">
                {pendingPayments.length} pago(s) pendiente(s) de verificación
              </p>
              <p className="text-sm text-amber-700">
                El sistema verifica automáticamente cada 5 minutos, pero puedes verificar manualmente.
              </p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-4">
          <Button
            variant={activeTab === "pending" ? "default" : "outline"}
            onClick={() => setActiveTab("pending")}
            className={activeTab === "pending" ? "bg-emerald-600 hover:bg-emerald-700" : ""}
          >
            <Clock className="w-4 h-4 mr-2" />
            Pendientes ({pendingPayments.length})
          </Button>
          <Button
            variant={activeTab === "history" ? "default" : "outline"}
            onClick={() => setActiveTab("history")}
            className={activeTab === "history" ? "bg-emerald-600 hover:bg-emerald-700" : ""}
          >
            <CreditCard className="w-4 h-4 mr-2" />
            Historial
          </Button>
        </div>

        {/* Content */}
        <div className="bg-white rounded-xl border border-navy-200 overflow-hidden">
          {activeTab === "pending" ? (
            pendingPayments.length === 0 ? (
              <div className="text-center py-12">
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                <p className="text-navy-700 font-medium">No hay pagos pendientes</p>
                <p className="text-navy-500 text-sm">Todos los pagos han sido procesados</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-navy-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-navy-600 uppercase">Fecha</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-navy-600 uppercase">Cliente</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-navy-600 uppercase">Producto</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-navy-600 uppercase">Monto</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-navy-600 uppercase">Estado</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-navy-600 uppercase">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-navy-100">
                    {pendingPayments.map((payment) => (
                      <motion.tr
                        key={payment.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="hover:bg-navy-50"
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-navy-400" />
                            <span className="text-sm text-navy-700">{formatDate(payment.created_at)}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4 text-navy-400" />
                            <span className="text-sm text-navy-700">
                              {payment.client_email || payment.customer_email || "-"}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm font-medium text-navy-900">
                            {payment.product_name || payment.product_id}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm font-bold text-emerald-600">
                            {formatAmount(payment.amount)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge status={payment.status} />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => verifyPayment(payment.id)}
                              disabled={verifying === payment.id}
                              className="gap-1"
                            >
                              {verifying === payment.id ? (
                                <RefreshCw className="w-3 h-3 animate-spin" />
                              ) : (
                                <Eye className="w-3 h-3" />
                              )}
                              Verificar
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => processPayment(payment.id)}
                              disabled={processing === payment.id}
                              className="gap-1 bg-emerald-600 hover:bg-emerald-700"
                            >
                              {processing === payment.id ? (
                                <RefreshCw className="w-3 h-3 animate-spin" />
                              ) : (
                                <Play className="w-3 h-3" />
                              )}
                              Procesar
                            </Button>
                          </div>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-navy-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-navy-600 uppercase">Fecha</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-navy-600 uppercase">Cliente</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-navy-600 uppercase">Producto</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-navy-600 uppercase">Monto</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-navy-600 uppercase">Estado</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-navy-600 uppercase">Stripe</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-navy-100">
                  {paymentHistory.map((payment) => (
                    <tr key={payment.id} className="hover:bg-navy-50">
                      <td className="px-4 py-3 text-sm text-navy-700">{formatDate(payment.created_at)}</td>
                      <td className="px-4 py-3 text-sm text-navy-700">
                        {payment.client_email || payment.customer_email || "-"}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-navy-900">
                        {payment.product_name || payment.product_id}
                      </td>
                      <td className="px-4 py-3 text-sm font-bold text-emerald-600">
                        {formatAmount(payment.amount)}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={payment.status} />
                      </td>
                      <td className="px-4 py-3">
                        {payment.stripe_payment_status && (
                          <span className="text-xs text-navy-500">
                            {payment.stripe_payment_status}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Info */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-4">
          <h3 className="font-semibold text-blue-800 mb-2">¿Cómo funciona?</h3>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• El sistema verifica automáticamente los pagos pendientes cada <strong>5 minutos</strong></li>
            <li>• Si un pago está confirmado en Stripe, se procesa automáticamente</li>
            <li>• Puedes verificar y procesar pagos manualmente usando los botones</li>
            <li>• "Verificar" consulta el estado en Stripe sin procesar</li>
            <li>• "Procesar" crea el cliente y caso si el pago está confirmado</li>
          </ul>
        </div>
      </div>
    </DashboardLayout>
  );
}
